import { useCallback, useState, type ReactNode } from 'react';
import {
  getActivePipeexProfile,
  saveCurrentConfigurationToPipeex,
} from '../../../shared/api/pipeex/pcfSettingsClient';
import { useConfigurationStore } from '../../../shared/stores/useConfigurationStore';
import { useSnackbarStore } from '../../../shared/stores/useSnackbarStore';
import { useUnsavedChangesDialog } from '../../../shared/ui/dialogs/useUnsavedChangesDialog';
import { webLog } from '../../../shared/api/grpc/webLogger';

/**
 * Export-time guard for unsaved mapping settings. The PCF export resolves its
 * settings from the saved mapping profile, so edits that still live only in
 * the configuration store (`isDirty`) would silently be left out. Before any
 * export entry point runs, ask the user to save first, export anyway, or
 * cancel.
 */
export function useUnsavedMappingSettingsGuard(): {
  /** Resolves true when the export may proceed. */
  confirmExportWithUnsavedSettings: () => Promise<boolean>;
  unsavedSettingsGuardDialog: ReactNode;
  savingUnsavedSettings: boolean;
} {
  const showSuccess = useSnackbarStore((state) => state.showSuccess);
  const showError = useSnackbarStore((state) => state.showError);
  const { requestChoice, unsavedChangesDialog } = useUnsavedChangesDialog();
  const [savingUnsavedSettings, setSavingUnsavedSettings] = useState(false);

  const confirmExportWithUnsavedSettings = useCallback(async (): Promise<boolean> => {
    if (!useConfigurationStore.getState().isDirty) {
      return true;
    }

    const choice = await requestChoice({
      title: 'Unsaved mapping settings',
      message:
        'Your mapping settings have unsaved changes. The export uses the last saved '
        + 'profile settings, so these changes will not be included unless you save them first.',
      saveLabel: 'Save & Export',
      discardLabel: 'Export without saving',
      cancelLabel: 'Cancel',
    });

    if (choice === 'cancel') {
      return false;
    }
    if (choice === 'discard') {
      webLog.info('export-orchestrator', '[unsaved-settings-guard] user chose export without saving');
      return true;
    }

    setSavingUnsavedSettings(true);
    try {
      const activeProfile = getActivePipeexProfile();
      await saveCurrentConfigurationToPipeex(activeProfile.name, activeProfile.settingsType);
      useConfigurationStore.getState().saveConfiguration();
      webLog.info(
        'export-orchestrator',
        `[unsaved-settings-guard] settings saved before export profile=${JSON.stringify(activeProfile.name)}`,
      );
      showSuccess('Mapping settings saved. Continuing with the export.');
      return true;
    } catch (error) {
      const detail = error instanceof Error ? error.message : String(error);
      webLog.warn('export-orchestrator', `[unsaved-settings-guard] save before export failed: ${detail}`);
      showError(`Mapping settings could not be saved: ${detail}`);
      return false;
    } finally {
      setSavingUnsavedSettings(false);
    }
  }, [requestChoice, showError, showSuccess]);

  return {
    confirmExportWithUnsavedSettings,
    unsavedSettingsGuardDialog: unsavedChangesDialog,
    savingUnsavedSettings,
  };
}
